import { GetServerSidePropsContext, InferGetServerSidePropsType } from "next";
import { useRouter } from "next/router";
import { useEffect } from "react";

import LayoutPage from "~/components/layout/layout-page";
import AddressList from "~/components/myaccount/addresses/address-list";
import Dashboard from "~/components/myaccount/dashboard";
import LoyaltyProgram from "~/components/myaccount/loyalty/loyalty-program";
import MyAccountPage from "~/components/myaccount/myaccount-page";
import OrderDetail from "~/components/myaccount/orders/order-detail";
import Orders from "~/components/myaccount/orders/orders";
import Payments from "~/components/myaccount/payments";
import PreferredStore from "~/components/myaccount/preferred-store";
import RequestReturn from "~/components/myaccount/returns/request-return";
import Settings from "~/components/myaccount/settings/settings";
import Wishlist from "~/components/myaccount/wishlist/wishlist";
import WishlistDetail from "~/components/myaccount/wishlist/wishlist-detail";
import { useAuthContext } from "~/contexts/auth";
import { usePageTracking } from "~/contexts/page-trackings";
import Logger from "~/services/logger/logger";
import appRoutes from "~/utils/app-routes";
import ssrUtils from "~/utils/ssr-utils";

type Props = InferGetServerSidePropsType<typeof getServerSideProps>;

export default function MyAccountContent(props: Props) {
  const router = useRouter();
  const { slug } = router.query;
  const { isAuthenticated, isLoadingUser } = useAuthContext();
  const firstLevel = Array.isArray(slug) ? slug[0] : slug;
  usePageTracking(`${["account", firstLevel].filter((e) => !!e).join(".")}`, {
    title: props.page?.seoMetadata?.fields.seoTitle,
  });

  // const completeSlug = Array.isArray(slug) ? slug.join("-") : slug;

  const renderRoute = () => {
    switch (firstLevel) {
      case "addresses":
        return <AddressList />;
      case "preferred-store":
        return <PreferredStore />;
      case "wishlist":
        if (Array.isArray(slug) && slug[1]) {
          return <WishlistDetail wishlistId={slug[1]} />;
        } else {
          return <Wishlist />;
        }
      case "settings":
        return <Settings />;
      case "loyalty":
        return <LoyaltyProgram editorialComponents={props.page?.components} />;
      case "payments":
        return <Payments />;
      case "purchases":
        if (Array.isArray(slug) && slug[1]) {
          if (slug[2] == "request-return") {
            return <RequestReturn orderId={slug[1]} />;
          } else {
            return <OrderDetail orderId={slug[1]} />;
          }
        } else {
          return <Orders />;
        }
      case "returns":
        if (Array.isArray(slug) && slug[1]) {
          return <OrderDetail returns orderId={slug[1]} returnReasonsTranslations={props.returnReasonsTranslations} />;
        } else {
          return <Orders returns />;
        }
      default:
        return <Dashboard />;
    }
  };

  // redirect not logged effect
  useEffect(() => {
    Logger.instance.debug("my-account: useEffect auth state", {
      isLoadingUser,
      isAuthenticated,
    });
    if (router && !isLoadingUser && !isAuthenticated) {
      Logger.instance.debug("my-account: redirect to login");
      router.replace(`/${router.locale}${appRoutes.LOGIN}`);
    }
  }, [isAuthenticated, router, isLoadingUser]);

  if (!isAuthenticated) {
    return <></>;
  }

  return (
    <LayoutPage {...props} navColor="gradient" hidePrefooter>
      <MyAccountPage>{renderRoute()}</MyAccountPage>
    </LayoutPage>
  );
}

export async function getServerSideProps(context: GetServerSidePropsContext) {
  if (context.locale === context.defaultLocale) {
    return {
      notFound: true,
    };
  }

  const calculatedSlug = Array.isArray(context.params?.slug) ? context.params?.slug.join("-") : context.params?.slug;
  const contentfulSlug = "my-account" + (calculatedSlug ? "/" + calculatedSlug : "");

  const pageData = await ssrUtils.getServerSideEditorialPageData(context, false, contentfulSlug);
  const returnReasonsTranslations = await ssrUtils.getServerSideEntityTranslations(context, "ReturnReason");
  // redirect to 404 in case of missing app settings
  if (
    context.params?.slug?.[2] == "request-return" &&
    (pageData.configuration["App:Ecommerce:EnableReturns"] != "true" ||
      process.env.NEXT_PUBLIC_RETURNS_ENABLED != "true")
  ) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      ...pageData,
      returnReasonsTranslations,
    },
  };
}
